import { Accordion } from "@/components/ui/accordion";
import React from "react";
import { useLocale, useTranslations } from "../../../helpers/translations";
import { generateLocalizedPath } from "../helpers/links";
import { MARKETING_ROUTES, RAILS_ROUTES } from "../helpers/routes";
import { MobileNavSubSection } from "../shared/MobileNavSubSection";
import { NavColumnGroup } from "../shared/NavColumnGroup";
import { NavSubColumn } from "../shared/NavSubColumn";
import { NavSubMenuSection } from "../shared/NavSubMenuSection";

type PlatformSubMenuProps = {
  compact?: boolean;
  wrapperComponent: React.ComponentType<{
    title: string;
    children: React.ReactNode;
  }>;
};

export const PlatformSubMenu = ({
  wrapperComponent,
  compact,
}: PlatformSubMenuProps) => {
  const t = useTranslations("nav.platform");
  const locale = useLocale();
  const WrapperComponent = wrapperComponent;

  if (!compact) {
    return (
      <WrapperComponent title={t("title")}>
        <NavSubMenuSection
          heading={t("col3.header")}
          items={[
            {
              title: t("col3.links.machine_motion_ai.title"),
              description: t("col3.links.machine_motion_ai.description"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINEMOTION_AI, locale),
            },
            {
              title: t("col3.links.griip_physical_ai.title"),
              description: t("col3.links.griip_physical_ai.description"),
              badge: t("col3.links.griip_physical_ai.badge"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.PHYSICAL_AI_PIPELINE,
                locale,
              ),
            },
          ]}
        />
        <NavSubMenuSection
          heading={t("col1.header")}
          items={[
            {
              title: t("col1.links.manufacturing_automation_platform.title"),
              href: generateLocalizedPath(
                RAILS_ROUTES.MANUFACTURING_AUTOMATION_PLATFORM,
                locale,
              ),
            },
            {
              title: t("col1.links.machine_builder.title"),
              description: t("col1.links.machine_builder.description"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_BUILDER, locale),
            },
            {
              title: t("col1.links.machine_logic.title"),
              description: t("col1.links.machine_logic.description"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_LOGIC, locale),
            },
            {
              title: t("col1.links.machine_analytics.title"),
              description: t("col1.links.machine_analytics.description"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_ANALYTICS, locale),
            },
            {
              title: t("col1.links.remote_support.title"),
              description: t("col1.links.remote_support.description"),
              href: generateLocalizedPath(RAILS_ROUTES.SERVICES_REMOTE_SUPPORT, locale),
            },
          ]}
          cta={{
            text: t("footer.text"),
            href: generateLocalizedPath(RAILS_ROUTES.SUBSCRIPTIONS, locale),
          }}
        />
        <NavColumnGroup heading={t("col2.header")}>
          <NavSubColumn
            subtitle={t("col2.sub_col1.header")}
            items={[
              {
                title: t("col2.sub_col1.links.manufacturing.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_MANUFACTURING,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.assembly.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_ASSEMBLY,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.material_handling.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_MATERIAL_HANDLING,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.end_of_line_packaging.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_EOL_PACKAGING,
                  locale,
                ),
              },
            ]}
            cta={{
              text: t("col2.sub_col1.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGNS_OFFICIAL, locale),
            }}
          />
          <NavSubColumn
            subtitle={t("col2.sub_col2.header")}
            items={[
              {
                title: t("col2.links.extrusions.title"),
                href: generateLocalizedPath(RAILS_ROUTES.HARDWARE, locale),
              },
              {
                title: t("col2.links.actuators.title"),
                href: generateLocalizedPath(RAILS_ROUTES.ACTUATORS, locale),
              },
              {
                title: t("col2.links.conveyors.title"),
                href: generateLocalizedPath(RAILS_ROUTES.CONVEYORS, locale),
              },
              {
                title: t("col2.links.robot_arms.title"),
                href: generateLocalizedPath(MARKETING_ROUTES.ROBOT_ARM, locale),
              },
            ]}
            cta={{
              text: t("col2.sub_col2.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
        </NavColumnGroup>
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent title={t("title")}>
      <Accordion type="multiple" className="w-full border-none">
        <MobileNavSubSection
          title={t("col3.header")}
          items={[
            {
              title: t("col3.links.machine_motion_ai.title"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINEMOTION_AI, locale),
            },
            {
              title: t("col3.links.griip_physical_ai.title"),
              href: generateLocalizedPath(
                MARKETING_ROUTES.PHYSICAL_AI_PIPELINE,
                locale,
              ),
              description: t("col3.links.griip_physical_ai.description"),
              badge: t("col3.links.griip_physical_ai.badge"),
            },
          ]}
        />
        <MobileNavSubSection
          title={t("col1.header")}
          items={[
            {
              title: t("col1.links.manufacturing_automation_platform.title"),
              href: generateLocalizedPath(
                RAILS_ROUTES.MANUFACTURING_AUTOMATION_PLATFORM,
                locale,
              ),
            },
            {
              title: t("col1.links.machine_builder.title"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_BUILDER, locale),
              description: t("col1.links.machine_builder.description"),
            },
            {
              title: t("col1.links.machine_logic.title"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_LOGIC, locale),
              description: t("col1.links.machine_logic.description"),
            },
            {
              title: t("col1.links.machine_analytics.title"),
              href: generateLocalizedPath(RAILS_ROUTES.MACHINE_ANALYTICS, locale),
              description: t("col1.links.machine_analytics.description"),
            },
            {
              title: t("col1.links.remote_support.title"),
              href: generateLocalizedPath(RAILS_ROUTES.SERVICES_REMOTE_SUPPORT, locale),
              description: t("col1.links.remote_support.description"),
            },
          ]}
          cta={{
            text: t("footer.text"),
            href: generateLocalizedPath(RAILS_ROUTES.SUBSCRIPTIONS, locale),
          }}
        />
        <MobileNavSubSection title={t("col2.header")}>
          <MobileNavSubSection
            title={t("col2.sub_col1.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("col2.sub_col1.links.manufacturing.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_MANUFACTURING,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.assembly.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_ASSEMBLY,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.material_handling.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_MATERIAL_HANDLING,
                  locale,
                ),
              },
              {
                title: t("col2.sub_col1.links.end_of_line_packaging.title"),
                href: generateLocalizedPath(
                  RAILS_ROUTES.DESIGN_TEMPLATES_EOL_PACKAGING,
                  locale,
                ),
              },
            ]}
            cta={{
              text: t("col2.sub_col1.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGNS_OFFICIAL, locale),
            }}
          />
          <MobileNavSubSection
            title={t("col2.sub_col2.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("col2.links.extrusions.title"),
                href: generateLocalizedPath(RAILS_ROUTES.HARDWARE, locale),
              },
              {
                title: t("col2.links.actuators.title"),
                href: generateLocalizedPath(RAILS_ROUTES.ACTUATORS, locale),
              },
              {
                title: t("col2.links.conveyors.title"),
                href: generateLocalizedPath(RAILS_ROUTES.CONVEYORS, locale),
              },
              {
                title: t("col2.links.robot_arms.title"),
                href: generateLocalizedPath(MARKETING_ROUTES.ROBOT_ARM, locale),
              },
            ]}
            cta={{
              text: t("col2.sub_col2.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
        </MobileNavSubSection>
      </Accordion>
    </WrapperComponent>
  );
};
